import React, { useState } from 'react';
import { Button, Form, Input, Card, message, Divider, Typography } from 'antd';
import {
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  MailOutlined,
  UserOutlined
} from '@ant-design/icons';
import { apiClient } from '../api/client';
import { CreateUserRequest } from '../types/api';
import { getRef } from '../api/utm';
import { APP_LOGO_URL as cleverooLogo } from '../constants/brand';

const { Title, Text, Link } = Typography;

// Reuse same animations as Login
const animations = `
  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
  @keyframes glow {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.8; }
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = animations;
  document.head.appendChild(styleSheet);
}

interface SignupFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface SignupUserData {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role?: string;
  studentId?: string;
  accessToken: string;
  refreshToken: string;
  provider?: string;
}

interface SignupProps {
  onSignup?: (userData: SignupUserData) => void;
  onSwitchToLogin?: () => void;
}

const Signup: React.FC<SignupProps> = ({ onSignup, onSwitchToLogin }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Detect referral code from URL or previously persisted value so we can
  // show a welcome banner and include it in the registration payload.
  const referralCode = React.useMemo(() => getRef(), []);

  const handleSignup = async (values: SignupFormData) => {
    setLoading(true);
    try {
      const refCode = getRef();
      const registerRequest: CreateUserRequest = {
        name: values.name,
        email: values.email,
        password: values.password,
        ...(refCode ? { referralCode: refCode } : {}),
      };

      const response = await apiClient.register(registerRequest);

      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);

      message.success('Account created successfully!');

      if (onSignup) {
        onSignup({
          email: response.user.email,
          name: response.user.name,
          id: response.user.id,
          role: response.user.role as string,
          studentId: response.user.studentId,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        });
      }
    } catch (error: any) {
      message.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const googleButtonRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!googleClientId) return;

    const initGoogle = () => {
      if (!window.google || !googleButtonRef.current) return false;

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response: any) => {
          setGoogleLoading(true);
          try {
            const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
            const apiResponse = await fetch(`${baseURL}/api/auth/google`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ credential: response.credential }),
            });

            const data = await apiResponse.json();

            if (data.success) {
              localStorage.setItem('accessToken', data.data.accessToken);
              localStorage.setItem('refreshToken', data.data.refreshToken);
              message.success('Account created with Google!');

              if (onSignup) {
                onSignup({
                  email: data.data.user.email,
                  name: data.data.user.name,
                  id: data.data.user.id,
                  avatar: data.data.user.avatar,
                  role: data.data.user.role,
                  studentId: data.data.user.studentId,
                  accessToken: data.data.accessToken,
                  refreshToken: data.data.refreshToken,
                  provider: 'google',
                });
              }
            } else {
              throw new Error(data.error || 'Google sign up failed');
            }
          } catch (error: any) {
            message.error(error.message || 'Google sign up failed');
          } finally {
            setGoogleLoading(false);
          }
        },
      });

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: googleButtonRef.current.offsetWidth,
        text: 'signup_with',
        shape: 'pill',
      });
      return true;
    };

    if (!initGoogle()) {
      const interval = setInterval(() => {
        if (initGoogle()) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [onSignup]);

  const inputStyle: React.CSSProperties = {
    height: '52px',
    borderRadius: '16px',
    border: '2px solid #e5e7eb',
    fontSize: '16px',
    fontWeight: 400,
    padding: '0 16px',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    transition: 'all 0.2s ease-in-out',
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = '#667eea';
    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = '#e5e7eb';
    e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
  };

  const labelStyle: React.CSSProperties = {
    color: '#374151',
    fontWeight: 600,
    fontSize: '14px',
    marginBottom: '8px',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradient 15s ease infinite',
      }}
    >
      {/* Background decorative elements */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            width: '320px', height: '320px', borderRadius: '50%', opacity: 0.2,
            top: '-10rem', right: '-10rem',
            background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
            filter: 'blur(40px)',
            animation: 'float 6s ease-in-out infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: '320px', height: '320px', borderRadius: '50%', opacity: 0.2,
            bottom: '-10rem', left: '-10rem',
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            filter: 'blur(40px)',
            animation: 'float 8s ease-in-out infinite',
            animationDelay: '1s',
          }}
        />
      </div>

      <div style={{ width: '100%', maxWidth: '28rem', position: 'relative', zIndex: 10 }}>
        {/* Logo and Title */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '1rem' }}>
            <div
              style={{
                position: 'relative',
                animation: 'float 6s ease-in-out infinite',
              }}
            >
              <div
                style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '50%', filter: 'blur(15px)', opacity: 0.4,
                  animation: 'glow 2s ease-in-out infinite alternate',
                }}
              />
              <img
                src={cleverooLogo}
                alt="Cleveroo Logo"
                style={{
                  position: 'relative',
                  width: '80px', height: '80px', maxWidth: '80px', maxHeight: '80px',
                  filter: 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.2))',
                  transition: 'transform 0.3s ease',
                  cursor: 'pointer',
                }}
              />
            </div>
          </div>
          <Title
            level={2}
            style={{
              fontSize: '2.5rem', fontWeight: 800,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text', textAlign: 'center',
              marginBottom: '1rem', fontFamily: '"Inter", sans-serif',
            }}
          >
            Create Your Account
          </Title>
          <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.125rem', textAlign: 'center', display: 'block' }}>
            Start your learning adventure with Cleveroo
          </Text>
        </div>

        {/* Referral welcome banner */}
        {referralCode && (
          <div
            style={{
              background: 'rgba(255,255,255,0.20)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: 16,
              padding: '12px 20px',
              marginBottom: '1.25rem',
              textAlign: 'center',
            }}
          >
            <Text style={{ color: 'white', fontSize: 15, fontWeight: 600 }}>
              You were invited by a friend — sign up and you both get a free month!
            </Text>
          </div>
        )}

        {/* Signup Card */}
        <Card
          style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.3)',
            borderRadius: '24px',
            backdropFilter: 'blur(20px)',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          }}
          bodyStyle={{ padding: '32px' }}
        >
          <Form form={form} name="signup" onFinish={handleSignup} layout="vertical" size="large" requiredMark={false}>
            <Form.Item
              name="name"
              label={<span style={labelStyle}>Full Name</span>}
              rules={[{ required: true, message: 'Please enter your name' }]}
              style={{ marginBottom: '16px' }}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#9ca3af', fontSize: '16px' }} />}
                placeholder="Enter your full name"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </Form.Item>

            <Form.Item
              name="email"
              label={<span style={labelStyle}>Email Address</span>}
              rules={[
                { required: true, message: 'Please enter your email address' },
                { type: 'email', message: 'Please enter a valid email address' },
              ]}
              style={{ marginBottom: '16px' }}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#9ca3af', fontSize: '16px' }} />}
                placeholder="Enter your email address"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span style={labelStyle}>Password</span>}
              rules={[
                { required: true, message: 'Please enter a password' },
                { min: 6, message: 'Password must be at least 6 characters' },
              ]}
              style={{ marginBottom: '16px' }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#9ca3af', fontSize: '16px' }} />}
                placeholder="Create a password"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={<span style={labelStyle}>Confirm Password</span>}
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
              style={{ marginBottom: '24px' }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#9ca3af', fontSize: '16px' }} />}
                placeholder="Confirm your password"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: '1.5rem' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  width: '100%', height: '56px', fontSize: '18px', fontWeight: 700,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  boxShadow: '0 10px 25px -5px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease', cursor: 'pointer', color: 'white',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(102, 126, 234, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(102, 126, 234, 0.4)';
                }}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </Form.Item>
          </Form>

          <Divider style={{ margin: '1.5rem 0', borderColor: '#e5e7eb' }}>
            <span style={{ color: '#9ca3af', fontSize: '14px', fontWeight: 600, backgroundColor: 'white', padding: '0 16px' }}>
              OR
            </span>
          </Divider>

          {/* Google Signup Button */}
          <div
            ref={googleButtonRef}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          />
          {googleLoading && (
            <div style={{ textAlign: 'center', marginTop: '8px', color: '#6b7280', fontSize: '14px' }}>
              Signing up with Google...
            </div>
          )}

          {/* Login Link */}
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Text style={{ color: '#6b7280', fontSize: '15px' }}>
              Already have an account?{' '}
              <Link
                onClick={onSwitchToLogin}
                style={{
                  color: '#667eea', fontWeight: 600,
                  textDecoration: 'none', cursor: 'pointer',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#4f46e5';
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#667eea';
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                Sign in
              </Link>
            </Text>
          </div>
        </Card>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '13px', lineHeight: '1.6' }}>
            By creating an account, you agree to our{' '}
            <Link
              style={{ color: 'rgba(255, 255, 255, 0.9)', textDecoration: 'none', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link
              style={{ color: 'rgba(255, 255, 255, 0.9)', textDecoration: 'none', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >
              Privacy Policy
            </Link>
          </Text>
        </div>
      </div>
    </div>
  );
};

export default Signup;
